import { useQuery } from '@tanstack/react-query'
import { fetchTransactions, fetchTransactionSummary } from '../../transactions/api'
import type { TransactionStatus, TransactionInvoiceStatus } from '../../../types/transactions'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatAppCurrency, formatAppDate } from '@/lib/locale'
import { useTranslation } from 'react-i18next'

const statusClass: Record<TransactionStatus, string> = {
  detected: 'border-gray-300 bg-gray-500/10 text-gray-700',
  pending: 'border-amber-300 bg-amber-500/10 text-amber-800',
  validated: 'border-blue-300 bg-blue-500/10 text-blue-800',
  rejected: 'border-red-300 bg-red-500/10 text-red-800',
  paid: 'border-green-400 bg-green-500/15 text-green-800',
}

const invoiceStatusClass: Record<NonNullable<TransactionInvoiceStatus>, string> = {
  pending: 'border-amber-300 bg-amber-500/10 text-amber-800',
  paid: 'border-green-400 bg-green-500/15 text-green-800',
  unpaid: 'border-red-300 bg-red-500/10 text-red-800',
  overdue: 'border-red-400 bg-red-500/15 text-red-900',
  cancelled: 'border-border bg-muted/40 text-muted-foreground',
}

export function IacrmInvoicesPanel() {
  const { t } = useTranslation()

  const summaryQuery = useQuery({
    queryKey: ['transactions', 'summary', 'iacrm'],
    queryFn: () => fetchTransactionSummary(),
  })

  const transactionsQuery = useQuery({
    queryKey: ['transactions', 'iacrm'],
    queryFn: () => fetchTransactions(),
  })

  const summary = summaryQuery.data?.data
  const transactions = transactionsQuery.data?.data ?? []

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid gap-3 md:grid-cols-3">
        <article className="rounded-lg border border-border bg-card px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('iacrm.panels.invoices.summary.totalInvoiced')}
          </p>
          <p className="mt-2 text-lg font-semibold text-foreground">
            {summary ? formatAppCurrency(summary.total_amount, 'EUR') : '—'}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('iacrm.panels.invoices.summary.invoiceCount', { count: summary?.transaction_count ?? 0 })}
          </p>
        </article>

        <article className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-700">
            {t('iacrm.panels.invoices.summary.paid')}
          </p>
          <p className="mt-2 text-lg font-semibold text-emerald-900">
            {summary ? formatAppCurrency(summary.paid_amount, 'EUR') : '—'}
          </p>
          <p className="text-xs text-emerald-700">
            {t('iacrm.panels.invoices.summary.invoiceCount', { count: summary?.status_breakdown?.paid ?? 0 })}
          </p>
        </article>

        <article className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-blue-700">
            Points attribués
          </p>
          <p className="mt-2 text-lg font-semibold text-blue-900">
            {summary?.points_awarded_total ?? 0} pts
          </p>
          <p className="text-xs text-blue-700">
            {summary?.linked_prospect_count ?? 0} filleul(s) concerné(s)
          </p>
        </article>
      </div>

      {/* Transactions table */}
      <article className="rounded-lg border border-border bg-card app-card-padding">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          {t('iacrm.panels.invoices.eyebrow')}
        </p>
        <h2 className="app-section-title mt-2">
          {t('iacrm.panels.invoices.title', { count: transactions.length })}
        </h2>

        {transactionsQuery.isPending ? (
          <p className="mt-4 text-sm text-muted-foreground">{t('iacrm.panels.invoices.loading')}</p>
        ) : transactionsQuery.isError ? (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {t('iacrm.panels.invoices.error')}
          </p>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Filleul</TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Facture</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Payé le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell className="font-mono text-sm font-medium">
                      {tx.transaction_reference}
                    </TableCell>
                    <TableCell className="text-sm">
                      {tx.prospect_name ?? tx.prospect?.contact_name ?? '—'}
                      {tx.prospect?.company_name ? (
                        <span className="block text-xs text-muted-foreground">{tx.prospect.company_name}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{tx.product_name}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatAppCurrency(tx.amount, tx.currency_code)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusClass[tx.status]}>
                        {tx.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {tx.invoice_status ? (
                        <Badge variant="outline" className={invoiceStatusClass[tx.invoice_status] ?? invoiceStatusClass.pending}>
                          {tx.invoice_status}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {tx.points_awarded != null ? (
                        <span className="font-semibold text-emerald-700">{tx.points_awarded} pts</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {tx.paid_at ? formatAppDate(tx.paid_at) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                      {t('iacrm.panels.invoices.empty')}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        )}
      </article>
    </div>
  )
}
